import React, { useState } from "react";
import { Button } from "./ui/button";
import { Menu, Moon, Sun } from "lucide-react";
import { useTheme } from "./ThemeContext";
import { QuiltAppIcon } from "./QuiltLogo";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { DemoRequestDialog } from "./DemoRequestDialog";
import { AnimatedMenu } from "./animated-menu";

export interface NavItem {
    label: string;
    id?: string;
    href?: string;
    selected?: boolean;
}

interface PageHeaderProps {
    navItems?: NavItem[];
    customAction?: React.ReactNode;
    customActionMobile?: React.ReactNode;
}

export function PageHeader({ navItems = [], customAction, customActionMobile }: PageHeaderProps) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    // On phones the page links (For You / For Providers) stay in the header, so keep that list short.
    // In-page section anchors and every action (theme, store buttons, Get Started, login) go in the burger menu.
    const pageLinks = navItems.filter((item) => item.href);
    const sectionLinks = navItems.filter((item) => !item.href);

    const mobileMenuAction = customActionMobile ?? customAction ?? (
        <DemoRequestDialog triggerText="Get Started" source="provider_get_started">
            <Button className="btn-primary text-white w-full h-auto py-3 text-lg">
                Get Started
            </Button>
        </DemoRequestDialog>
    );

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 80; // Account for fixed header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const handleLogoClick = () => {
        if (location.pathname === '/') {
            const hero = document.getElementById('hero');
            if (hero) {
                scrollToSection('hero');
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            navigate('/');
        }
    };

    return (
        <>
            <header className={theme === 'dark'
                ? "fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-zinc-950/80 to-fuchsia-950/20 backdrop-blur-lg border-t border-t-fuchsia-300/20 border-b border-b-cyan-50/15 md:m-2 md:rounded-full"
                : "fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-white/80 to-white/20 backdrop-blur-lg border-t border-t-fuchsia-300/20 border-b border-b border-b-cyan-300/10 md:m-2 md:rounded-full"
            }>
                <div className="w-full max-w-[1440px] mx-auto px-4">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3 md:gap-8">
                            <button
                                onClick={handleLogoClick}
                                aria-label="Quilt home"
                                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                            >
                                <QuiltAppIcon className="w-10 h-10" />
                                {/* Wordmark drops out on very narrow phones so the page links still fit */}
                                <span className={`text-xl max-[359px]:hidden ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                                    Quilt
                                </span>
                            </button>

                            <nav
                                aria-label="Primary"
                                className={`${pageLinks.length > 0 ? 'flex' : 'hidden md:flex'} items-center gap-1 md:gap-4 text-sm`}
                            >
                                {navItems.map((item) => (
                                    item.href ? (
                                        <Link
                                            key={item.label}
                                            to={item.href}
                                            aria-current={item.selected ? "page" : undefined}
                                            className={`relative inline-flex min-h-11 items-center px-2 md:min-h-0 md:px-0 ${theme === 'dark'
                                                ? "text-slate-400 hover:text-fuchsia-400 transition-colors"
                                                : "text-slate-700 hover:text-fuchsia-600 transition-colors"
                                                }`}
                                        >
                                            <span className={item.selected ? "underline decoration-fuchsia-400 underline-offset-[6px]" : ""}>{item.label}</span>

                                        </Link>
                                    ) : (
                                        <button
                                            key={item.id}
                                            onClick={() => item.id && scrollToSection(item.id)}
                                            className={`relative hidden md:inline-flex ${theme === 'dark'
                                                ? "text-slate-400 hover:text-indigo-400 transition-colors"
                                                : "text-slate-700 hover:text-indigo-600 transition-colors"
                                                }`}
                                        >
                                            <span className={item.selected ? "underline underline-offset-[6px]" : ""}>{item.label}</span>

                                        </button>
                                    )
                                ))}
                            </nav>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Theme toggle and actions are desktop-only here; phones get them in the burger menu */}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleTheme}
                                aria-label="Toggle theme"
                                className={`hidden md:inline-flex ${theme === 'dark'
                                    ? "text-slate-400 hover:text-white hover:bg-slate-900"
                                    : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                                    }`}
                            >
                                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            </Button>
                            <div className="hidden md:flex items-center gap-2">
                                {customAction ? customAction : (
                                    <DemoRequestDialog triggerText="Get Started" source="provider_get_started">
                                        <Button
                                            className="btn-primary text-white"
                                        >
                                            Get Started
                                        </Button>
                                    </DemoRequestDialog>

                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                aria-label="Open menu"
                                aria-expanded={isMobileMenuOpen}
                                className={`size-11 md:hidden ${theme === 'dark'
                                    ? "text-slate-400 hover:bg-slate-900"
                                    : "text-slate-700 hover:bg-slate-100"
                                    }`}
                            >
                                <Menu className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>

                </div>
            </header>

            <AnimatedMenu
                isOpen={isMobileMenuOpen}
                setIsOpen={setIsMobileMenuOpen}
                isDark={theme === 'dark'}
                menuItems={sectionLinks}
                scrollToSection={scrollToSection}
                customActionMobile={mobileMenuAction}
            />
        </>
    );
}
